import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  TaskCodeWithCurrentRate,
  EntryPreviewRequest,
  EntryCompliancePreview,
} from '@renewal/types';
import { useFunds } from '../hooks/useFunds.js';
import {
  fillerFundReason,
  fillerFundSuffix,
  fundTakesFiller,
  isFillerTaskCode,
  weekAfterEntry,
} from '../utils/weekly-target.js';
import './TaskAssignmentPopover.css';

interface TaskAssignmentPopoverProps {
  taskCodes: TaskCodeWithCurrentRate[];
  startTime: string;
  endTime: string;
  dayCount: number;
  employeeAge: number;
  workDates: string[]; // Actual dates for the selected days
  schoolDayStatus: boolean[]; // Is each day a school day
  /** The week's whole minutes as the server counted them (before this entry). */
  weekMinutes?: number;
  /** 2400 when the 40-hour rule covers the week, null for earlier weeks. */
  requiredMinutes?: number | null;
  onPreview?: (entry: EntryPreviewRequest) => Promise<EntryCompliancePreview | null>;
  onAssign: (
    taskCodeId: string,
    supervisorName?: string,
    mealBreakConfirmed?: boolean,
    notes?: string,
    fundId?: number | null
  ) => Promise<void>;
  onClose: () => void;
}

/**
 * Format time like "9:00 AM"
 */
function formatTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const hour12 = (hours ?? 0) > 12 ? (hours ?? 0) - 12 : (hours ?? 0) === 0 ? 12 : (hours ?? 0);
  const ampm = (hours ?? 0) >= 12 ? 'PM' : 'AM';
  return `${hour12}:${String(minutes ?? 0).padStart(2, '0')} ${ampm}`;
}

/**
 * Whole minutes from start to end time
 */
function calculateMinutes(startTime: string, endTime: string): number {
  const [startH, startM] = startTime.split(':').map(Number);
  const [endH, endM] = endTime.split(':').map(Number);
  const startMinutes = (startH ?? 0) * 60 + (startM ?? 0);
  const endMinutes = (endH ?? 0) * 60 + (endM ?? 0);
  return endMinutes - startMinutes;
}

export function TaskAssignmentPopover({
  taskCodes,
  startTime,
  endTime,
  dayCount,
  employeeAge,
  workDates,
  schoolDayStatus,
  weekMinutes,
  requiredMinutes = null,
  onPreview,
  onAssign,
  onClose,
}: TaskAssignmentPopoverProps) {
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [selectedFundId, setSelectedFundId] = useState('');
  const [fundNotice, setFundNotice] = useState<string | null>(null);
  const [supervisorName, setSupervisorName] = useState('');
  const [mealBreakConfirmed, setMealBreakConfirmed] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { funds, loading: loadingFunds } = useFunds();

  // Compliance preview state
  const [preview, setPreview] = useState<EntryCompliancePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  // Only the latest preview request may set the result (task and fund can
  // change faster than the server answers).
  const previewRequestRef = useRef(0);

  const selectedTask = taskCodes.find((tc) => tc.id === selectedTaskId);
  const blockMinutes = calculateMinutes(startTime, endTime);
  const hours = blockMinutes / 60;
  const isMinor = employeeAge < 18;
  // The filler code (time not worked) may be charged only to an unrestricted
  // fund; the server refuses anything else, so the picker does not offer it.
  const isFiller = isFillerTaskCode(selectedTask?.code);
  const fundAllowed = (fund: (typeof funds)[number]) => !isFiller || fundTakesFiller(fund);

  // Fetch compliance preview when task or fund changes
  const fetchPreview = useCallback(async () => {
    if (!onPreview || !workDates.length) return;

    // Use first selected day for preview (most conservative check)
    const firstDate = workDates[0];
    const firstSchoolDay = schoolDayStatus[0] ?? false;
    const requestId = ++previewRequestRef.current;

    setLoadingPreview(true);
    try {
      const result = await onPreview({
        workDate: firstDate!,
        startTime,
        endTime,
        taskCodeId: selectedTaskId || taskCodes[0]?.id || '',
        isSchoolDay: firstSchoolDay,
        // The fund only means something once a task is chosen (the filler-fund
        // rule pairs the two).
        fundId: selectedTaskId ? (selectedFundId ? Number(selectedFundId) : null) : undefined,
        dayCount,
      });
      if (requestId === previewRequestRef.current) setPreview(result);
    } catch {
      // Preview failed, continue without it
      if (requestId === previewRequestRef.current) setPreview(null);
    } finally {
      if (requestId === previewRequestRef.current) setLoadingPreview(false);
    }
  }, [
    onPreview,
    workDates,
    schoolDayStatus,
    startTime,
    endTime,
    selectedTaskId,
    selectedFundId,
    dayCount,
    taskCodes,
  ]);

  // Fetch preview on mount and when task or fund changes
  useEffect(() => {
    if (selectedTaskId) {
      fetchPreview();
    }
  }, [selectedTaskId, fetchPreview]);

  // Initial preview without task (for the week line and requirements)
  useEffect(() => {
    if (taskCodes.length > 0 && !selectedTaskId) {
      fetchPreview();
    }
  }, [taskCodes, selectedTaskId, fetchPreview]);

  // Use preview requirements if available, otherwise fall back to task-based checks
  const needsSupervisor =
    preview?.requirements.supervisorRequired ??
    (selectedTask &&
      (selectedTask.supervisorRequired === 'always' ||
        (selectedTask.supervisorRequired === 'for_minors' && isMinor)));

  const needsMealBreak = preview?.requirements.mealBreakRequired ?? (hours > 6 && isMinor);

  // Check if there are blocking violations
  const hasViolations = Boolean(preview && preview.violations.length > 0);

  // Where the week lands if this is saved on every dragged day. The preview's
  // minutes when it has them, else the server's week total plus this block.
  const weekLine = preview?.weeklyTarget
    ? weekAfterEntry({
        afterMinutes: preview.weeklyTarget.afterMinutes,
        requiredMinutes: preview.weeklyTarget.requiredMinutes,
        dayCount,
      })
    : weekMinutes !== undefined
      ? weekAfterEntry({
          afterMinutes: weekMinutes + blockMinutes * dayCount,
          requiredMinutes,
          dayCount,
        })
      : null;

  const handleTaskChange = (taskId: string) => {
    setSelectedTaskId(taskId);
    const task = taskCodes.find((tc) => tc.id === taskId);
    const fund = funds.find((f) => String(f.id) === selectedFundId);
    // Switching to the filler clears a fund it cannot be charged to.
    if (task && isFillerTaskCode(task.code) && fund && !fundTakesFiller(fund)) {
      setSelectedFundId('');
      setFundNotice(
        `Funding source changed to General Fund: ${task.code} cannot go on ${fund.name}.`
      );
    }
  };

  const handleFundChange = (fundId: string) => {
    setSelectedFundId(fundId);
    setFundNotice(null);
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTaskId) {
      setError('Please select a task');
      return;
    }

    if (needsSupervisor && !supervisorName.trim()) {
      setError('Supervisor name is required for this task');
      return;
    }

    if (needsMealBreak && !mealBreakConfirmed) {
      setError('Please confirm you took a meal break');
      return;
    }

    const selectedFund = funds.find((f) => String(f.id) === selectedFundId);
    if (selectedTask && selectedFund && !fundAllowed(selectedFund)) {
      setError(fillerFundReason(selectedTask.code, selectedTask.name));
      return;
    }

    setSubmitting(true);
    try {
      await onAssign(
        selectedTaskId,
        needsSupervisor ? supervisorName : undefined,
        needsMealBreak ? mealBreakConfirmed : undefined,
        notes.trim() || undefined,
        selectedFundId ? Number(selectedFundId) : null
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create entry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="task-assignment-backdrop" onClick={onClose} role="presentation">
      <div
        className="task-assignment-popover"
        onClick={(e) => e.stopPropagation()}
        data-testid="task-assignment-popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="popover-title"
        aria-describedby="popover-info"
      >
        <div className="popover-header">
          <h4 id="popover-title">Assign Task</h4>
          <button
            className="close-btn"
            onClick={onClose}
            data-testid="popover-close"
            aria-label="Close dialog"
          >
            ×
          </button>
        </div>

        <div className="popover-info" id="popover-info">
          <span className="time-range">
            {formatTime(startTime)} - {formatTime(endTime)}
          </span>
          <span className="hours-badge">{hours.toFixed(1)}h</span>
          {dayCount > 1 && <span className="days-badge">× {dayCount} days</span>}
        </div>

        {/* Compliance preview info */}
        {loadingPreview && (
          <div
            className="compliance-loading"
            data-testid="compliance-loading"
            role="status"
            aria-busy="true"
            aria-label="Checking compliance"
          >
            Checking compliance...
          </div>
        )}

        {preview && !loadingPreview && (
          <div
            className="compliance-preview"
            data-testid="compliance-preview"
            aria-busy={loadingPreview}
          >
            {/* Violations (blocking) */}
            {preview.violations.length > 0 && (
              <div
                className="compliance-violations"
                data-testid="compliance-violations"
                role="alert"
                aria-live="assertive"
              >
                <div className="violation-header">Cannot create entry:</div>
                {preview.violations.map((v, i) => (
                  <div key={i} className="violation-item">
                    <span className="violation-icon" aria-hidden="true">
                      ⛔
                    </span>
                    <span className="violation-message">{v.message}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Warnings (informational) */}
            {preview.warnings.length > 0 && (
              <div
                className="compliance-warnings"
                data-testid="compliance-warnings"
                role="status"
                aria-live="polite"
              >
                {preview.warnings.map((w, i) => (
                  <div key={i} className="warning-item">
                    <span className="warning-icon" aria-hidden="true">
                      ⚠️
                    </span>
                    <span className="warning-message">{w.message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* The week against 40 hours. Information only: drafts may go over 40, and
            landing on 40 is not "ready" (a dragged day may be after today, or
            the week may have another blocker); the Submit line says when it is. */}
        {weekLine && !loadingPreview && (
          <div
            className={`popover-week-line popover-week-line--${weekLine.state}`}
            data-testid="popover-week-line"
          >
            {weekLine.state === 'over' && (
              <span className="popover-week-line-icon" aria-hidden="true">
                ⚠
              </span>
            )}
            <span>{weekLine.text}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {error && (
            <div className="popover-error" role="alert" data-testid="error-popover">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="taskCodeId">Task *</label>
            <select
              id="taskCodeId"
              value={selectedTaskId}
              onChange={(e) => handleTaskChange(e.target.value)}
              required
              data-testid="popover-task-select"
            >
              <option value="">Select a task...</option>
              {taskCodes.map((tc) => (
                <option key={tc.id} value={tc.id}>
                  {tc.code} - {tc.name} (${tc.currentRate.toFixed(2)}/hr)
                </option>
              ))}
            </select>
          </div>

          {funds.length > 0 && (
            <div className="form-group">
              <label htmlFor="fundId">Funding Source</label>
              {loadingFunds ? (
                <div className="loading-tasks">Loading funds...</div>
              ) : (
                <select
                  id="fundId"
                  value={selectedFundId}
                  onChange={(e) => handleFundChange(e.target.value)}
                  aria-describedby={isFiller ? 'popover-filler-fund-reason' : undefined}
                  data-testid="popover-fund-select"
                >
                  <option value="">General Fund (Default)</option>
                  {funds.map((fund) => {
                    const allowed = fundAllowed(fund);
                    return (
                      <option key={fund.id} value={fund.id} disabled={!allowed}>
                        {fund.name}
                        {allowed ? '' : fillerFundSuffix(fund)}
                      </option>
                    );
                  })}
                </select>
              )}
              {isFiller && selectedTask && (
                <span
                  className="help-text"
                  id="popover-filler-fund-reason"
                  data-testid="popover-filler-fund-reason"
                >
                  {fillerFundReason(selectedTask.code, selectedTask.name)}
                </span>
              )}
              {fundNotice && (
                <span className="help-text" role="status" data-testid="popover-fund-notice">
                  {fundNotice}
                </span>
              )}
            </div>
          )}

          {needsSupervisor && (
            <div className="form-group">
              <label htmlFor="supervisorName">Supervisor Present *</label>
              <input
                id="supervisorName"
                type="text"
                value={supervisorName}
                onChange={(e) => setSupervisorName(e.target.value)}
                placeholder="Name of supervising adult"
                required
                aria-describedby="supervisor-help"
                data-testid="popover-supervisor-input"
              />
              <span className="help-text" id="supervisor-help">
                {preview?.requirements.supervisorReason ||
                  'This task requires a supervisor to be present'}
              </span>
            </div>
          )}

          {needsMealBreak && (
            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={mealBreakConfirmed}
                  onChange={(e) => setMealBreakConfirmed(e.target.checked)}
                  aria-describedby="mealbreak-help"
                  data-testid="popover-mealbreak-checkbox"
                />
                I confirm I will take a 30-minute meal break during this shift
              </label>
              <span className="help-text" id="mealbreak-help">
                {preview?.requirements.mealBreakReason ||
                  'Required for shifts over 6 hours (workers under 18)'}
              </span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="popover-notes">Notes</label>
            <textarea
              id="popover-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional — e.g., invoice reference, task description"
              maxLength={500}
              rows={2}
              data-testid="popover-notes"
            />
            {notes.length > 0 && <span className="help-text">{notes.length}/500</span>}
          </div>

          <div className="popover-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={submitting}
              data-testid="popover-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`assign-btn ${hasViolations ? 'has-violations' : ''}`}
              disabled={submitting || !selectedTaskId || hasViolations}
              data-testid="popover-assign"
              title={hasViolations ? 'Cannot create entry due to compliance violations' : undefined}
            >
              {submitting
                ? 'Creating...'
                : hasViolations
                  ? 'Cannot Create'
                  : dayCount > 1
                    ? `Create ${dayCount} Entries`
                    : 'Create Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
